import * as React from 'react'
import PropTypes from 'prop-types'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import LogoM from '../inumet/logo/LogoM'
import { DialogTitle, Grid, IconButton } from '@mui/material'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { useEffect } from 'react'

import './dialogosMarino.css'

function PronosMarinoDialog({
    dialogTitle,
    exportToPdf,
    open,
    pathPrefixLogo,
    handleClose,
    dialogContent,
}) {
    const theme = useTheme()
    const mcel = useMediaQuery(theme.breakpoints.down('md'))

    const dialogRef = React.useRef()

    useEffect(() => {
        const handleBackButton = (event) => {
            event.preventDefault()
            handleClose()
            window.history.pushState(null, null, window.location.href)
        }

        if (open) {
            window.history.pushState(null, null, window.location.href)
            window.addEventListener('popstate', handleBackButton)
        } else {
            window.removeEventListener('popstate', handleBackButton)
        }

        return () => {
            window.removeEventListener('popstate', handleBackButton)
        }
    }, [open, handleClose])


    const handleExportPDF = async () => {
        const el = dialogRef.current
        if (!el) return

        // Tamaño A4
        const PDF_W_MM = 210
        const PDF_H_MM = 297

        // Objetivo de nitidez: ~180–200 DPI al ancho de A4
        const TARGET_DPI = 190
        const A4_WIDTH_IN = 8.27
        const TARGET_PX_W = Math.floor(TARGET_DPI * A4_WIDTH_IN) // ≈1570 px

        const elWidth = el.offsetWidth || el.clientWidth || 800
        const scale = Math.min(2, TARGET_PX_W / elWidth) // límite 2x para no exagerar

        // Render del HTML
        const canvas = await html2canvas(el, {
            scale,
            backgroundColor: '#ffffff',
            useCORS: true,
            // html2canvas respeta imageSmoothing del destino cuando re-dibujamos en el slice
        })

        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
            compress: true
        })

        const cW = canvas.width
        const cH = canvas.height

        // px/mm para mantener proporciones al ancho del PDF
        const pxPerMm = cW / PDF_W_MM
        const pagePxHeight = Math.floor(PDF_H_MM * pxPerMm)

        // Canvas temporal para “rebanadas”
        const pageCanvas = document.createElement('canvas')
        const pageCtx = pageCanvas.getContext('2d', { willReadFrequently: true })
        pageCanvas.width = cW
        pageCanvas.height = Math.min(pagePxHeight, cH)

        // Mejora de nitidez al reescalar
        pageCtx.imageSmoothingEnabled = true
        pageCtx.imageSmoothingQuality = 'high'

        // Usa WEBP si el navegador lo soporta y tu jsPDF es ≥ 2.5 (soporta WEBP).
        const webpSupport = (() => {
            try { return canvas.toDataURL('image/webp').startsWith('data:image/webp') } catch { return false }
        })()
        const IMG_MIME = webpSupport ? 'image/webp' : 'image/jpeg'
        const IMG_TYPE = webpSupport ? 'WEBP' : 'JPEG'

        // Calidad: un poco más alta que antes
        const QUALITY = webpSupport ? 0.75 : 0.78

        let rendered = 0
        let first = true

        while (rendered < cH) {
            const sliceHeight = Math.min(pagePxHeight, cH - rendered)
            if (pageCanvas.height !== sliceHeight) pageCanvas.height = sliceHeight

            // Fondo blanco + copia de la sección
            pageCtx.save()
            pageCtx.fillStyle = '#ffffff'
            pageCtx.fillRect(0, 0, cW, sliceHeight)
            pageCtx.drawImage(canvas, 0, rendered, cW, sliceHeight, 0, 0, cW, sliceHeight)
            pageCtx.restore()

            const dataUrl = pageCanvas.toDataURL(IMG_MIME, QUALITY)
            const sliceH_mm = sliceHeight / pxPerMm

            if (!first) pdf.addPage()
            pdf.addImage(dataUrl, IMG_TYPE, 0, 0, PDF_W_MM, sliceH_mm, undefined, 'FAST')

            rendered += sliceHeight
            first = false
        }

        pdf.save('marino_inumet.pdf')
    }

    let compDialogTitle = <span className="titulo">{dialogTitle}</span>

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                fullScreen={mcel}
                maxWidth={'md'}
                open={open}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
                PaperProps={{
                    sx: {
                        backgroundColor: '#f2f2f2',
                    },
                }}
            >
                <div ref={dialogRef}>
                    <DialogTitle className="dialog-title">
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Grid item key="g1">
                                <LogoM
                                    pathPrefix={pathPrefixLogo}
                                    mr={3}
                                    width={160}
                                    completoTransp
                                />
                            </Grid>
                            {!mcel && (
                                <Grid item key="g2">
                                    {compDialogTitle}
                                </Grid>
                            )}
                            <Grid item key="g3">
                                <IconButton onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                        {mcel && (
                            <Grid
                                key="g4"
                                container
                                direction="column"
                                alignItems="flex-start"
                            >
                                <Grid item key="g5">
                                    {compDialogTitle}
                                </Grid>
                            </Grid>
                        )}
                    </DialogTitle>

                    <DialogContent>{dialogContent}</DialogContent>
                </div>
                <DialogActions>
                    <Button key="bclose" onClick={handleClose}>
                        Cerrar
                    </Button>
                    {exportToPdf && (
                        <Button
                            onClick={handleExportPDF}
                            color="primary"
                            startIcon={<DownloadIcon />}
                            key="bexportpdf"
                        >
                            Exportar a PDF
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default PronosMarinoDialog

PronosMarinoDialog.propTypes = {
    dialogTitle: PropTypes.string,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    pathPrefixLogo: PropTypes.string,
    dialogContent: PropTypes.element,
    exportToPdf: PropTypes.bool,
}
